import React from "react";
import { useTranslation } from "react-i18next";
import { MdKeyboardArrowRight, MdKeyboardArrowLeft } from "react-icons/md";

type PaginationProps = {
  currentPage: number;
  lastPage: number;
  onPageChange: (page: number) => void;
};

const Pagination: React.FC<PaginationProps> = ({ currentPage, lastPage, onPageChange }) => {
  const { i18n } = useTranslation();

  if (lastPage <= 1) return null;

  const handlePageChange = (page: number) => {
    if (page < 1 || page > lastPage) return;
    onPageChange(page);
    window.scrollTo(0, 0);
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 3;

    // Always show first page
    if (currentPage > maxVisible + 2) {
      pages.push(1, "...");
    } else {
      for (let i = 1; i < currentPage; i++) pages.push(i);
    }

    // Show current page range
    for (
      let i = Math.max(1, currentPage - maxVisible);
      i <= Math.min(lastPage, currentPage + maxVisible);
      i++
    ) {
      pages.push(i);
    }

    // Always show last page
    if (currentPage < lastPage - maxVisible - 1) {
      pages.push("...", lastPage);
    } else {
      for (let i = currentPage + 1; i <= lastPage; i++) pages.push(i);
    }

    return Array.from(new Set(pages)); // remove duplicates
  };

  return (
    <div className="w-full flex-center py-5">
      <div className="flex gap-2 flex-wrap">
        {/* Prev */}
        <button
          disabled={currentPage === 1}
          onClick={() => handlePageChange(currentPage - 1)}
          className="px-3 py-1 border rounded disabled:opacity-50"
        >
          {i18n.language === "ar" ? <MdKeyboardArrowRight /> : <MdKeyboardArrowLeft />}
        </button>

        {/* Page Numbers */}
        {getPageNumbers().map((page, idx) =>
          typeof page === "number" ? (
            <button
              key={idx}
              className={`px-3 py-1 border rounded ${
                page === currentPage ? "bg-secondry text-white font-bold" : ""
              }`}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </button>
          ) : (
            <span key={idx} className="px-2 py-1 text-gray-500">
              {page}
            </span>
          )
        )}

        {/* Next */}
        <button
          disabled={currentPage === lastPage}
          onClick={() => handlePageChange(currentPage + 1)}
          className="px-3 py-1 border rounded disabled:opacity-50"
        >
          {i18n.language === "ar" ? <MdKeyboardArrowLeft /> : <MdKeyboardArrowRight />}
        </button>
      </div>
    </div>
  );
};

export default Pagination;
